<div class="container-fluid mat-elevation-z8 block-allow">
  <div class="loading-shade" *ngIf="isLoading || hasError">
    <mat-spinner *ngIf="isLoading"></mat-spinner>
    <div class="has-error" *ngIf="hasError">
      There was an error loading the country policies.
    </div>
  </div>

  <div class="w-100 p-3 head">
    <div>
      <h2 class="page-title">Block / Allow</h2>
      <p class="page-hint">
        Every country-level control the API enforces. The list committed in the repository is the
        default; saving here records a deliberate override on top of it, takes effect on the live
        API within seconds, and can be put back per control with one click.
      </p>
    </div>

    <div class="head-actions">
      <mat-button-toggle-group [value]="view" (change)="view = $event.value" class="view-toggle">
        <mat-button-toggle value="map">Map view</mat-button-toggle>
        <mat-button-toggle value="list">List view</mat-button-toggle>
      </mat-button-toggle-group>
      <button mat-stroked-button (click)="revert()" [disabled]="!isDirty || isSaving">Revert</button>
      <button mat-flat-button color="primary" (click)="save()" [disabled]="!isDirty || isSaving">
        {{ isSaving ? 'Saving…' : 'Save changes' }}
      </button>
    </div>
  </div>

  <!-- What a save would change, spelled out before it happens. -->
  <div class="w-100 px-3 pending" *ngIf="isDirty">
    <span class="pending-title">Unsaved:</span>
    <span class="pending-item" *ngFor="let change of changedPolicies">
      <b>{{ change.policy.label }}</b>
      <span class="add" *ngIf="change.added.length">+{{ change.added.join(', ') }}</span>
      <span class="remove" *ngIf="change.removed.length">−{{ change.removed.join(', ') }}</span>
      <span class="toggle" *ngIf="change.toggled">{{ enabledFor(change.policy.key) ? 'switched on' : 'switched off' }}</span>
    </span>
  </div>

  <div class="w-100 px-3 legend">
    <span class="legend-item"><span class="swatch blocked"></span> Blocked <b>{{ counts.blocked }}</b></span>
    <span class="legend-item"><span class="swatch allowed"></span> Explicitly allowed <b>{{ counts.allowed }}</b></span>
    <span class="legend-item"><span class="swatch nothing"></span> No entry <b>{{ counts.nothing }}</b></span>
    <mat-form-field class="search-field">
      <mat-label>Search country, code or dial code</mat-label>
      <input matInput [(ngModel)]="search" placeholder="Turkey, TR, TUR, +90">
    </mat-form-field>
  </div>

  <!-- One tab per control, so each list is looked at on its own. -->
  <div class="w-100 px-3 policy-tabs">
    <button class="policy-tab" *ngFor="let policy of policies; trackBy: trackByKey"
            [class.active]="view === policy.key"
            [title]="policy.description"
            (click)="view = policy.key">
      <span class="tab-label">{{ policyShortLabels[policy.key] || policy.label }}</span>
      <span class="tab-count">{{ draftCount(policy.key) }}</span>
      <span class="tab-flag override" *ngIf="policy.is_overridden">override</span>
      <span class="tab-flag off" *ngIf="!enabledFor(policy.key)">off</span>
    </button>
  </div>

  <!-- A single control, on its own. -->
  <div class="w-100 px-3 pb-3 policy-view" *ngIf="activePolicy as policy">
    <div class="policy-head">
      <div class="policy-meta">
        <h3 class="group-title">{{ policy.label }}
          <span class="group-count">{{ draftCount(policy.key) }}</span>
          <span class="tab-flag override" *ngIf="policy.is_overridden">override</span>
        </h3>
        <div class="policy-effect" [class.allow]="policy.effect === 'allow'">
          {{ policy.effect === 'allow' ? 'Allowlist — anything not listed is refused' : 'Block list — listed entries are blocked' }}
          · {{ policy.code_type | uppercase }}
        </div>
        <p class="value-description">{{ policy.description }}</p>
        <p class="policy-origin" *ngIf="policy.is_overridden">
          Overridden from this dashboard<span *ngIf="policy.updated_by"> by {{ policy.updated_by }}</span>;
          the committed default has {{ policy.default_countries.length }}.
        </p>
        <p class="policy-origin repo" *ngIf="!policy.is_overridden">Using the committed default from the repository.</p>
      </div>
      <div class="policy-actions">
        <mat-slide-toggle [checked]="enabledFor(policy.key)"
                          (change)="setEnabled(policy.key, $event.checked)">
          {{ enabledFor(policy.key) ? 'Control is on' : 'Control is off' }}
        </mat-slide-toggle>
        <button mat-stroked-button color="warn" *ngIf="policy.is_overridden"
                (click)="resetPolicyToDefault(policy)">Reset to repo default</button>
      </div>
    </div>

    <!-- Country-keyed control: one row per country. -->
    <ng-container *ngIf="policy.code_type === 'iso2' || policy.code_type === 'iso3'">
      <div class="policy-toolbar">
        <button mat-stroked-button (click)="addAllShown()">Add all shown</button>
        <button mat-stroked-button (click)="removeAllShown()">Remove all shown</button>
        <span class="toolbar-note">{{ policyRows.length }} shown</span>
      </div>
      <table class="country-table policy-table">
        <colgroup><col class="c-country"><col class="c-overall"><col class="c-stat"><col class="c-stat"><col class="c-stat"></colgroup>
        <thead>
          <tr>
            <th>Country</th>
            <th>This control</th>
            <th class="num">Users</th>
            <th class="num">SMS req.</th>
            <th class="num">Numbers</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of policyRows; trackBy: trackByIso2"
              [ngClass]="'row-' + row.states[policy.key]">
            <td class="col-country" (click)="toggleCell(row, policy.key)">
              <span class="row-flag">{{ row.flag }}</span>
              <span class="row-name">{{ row.name }}</span>
              <span class="row-codes">{{ codeFor(row, policy) }}<span *ngIf="row.calling_code"> · {{ row.calling_code }}</span></span>
              <span class="row-pin" *ngIf="row.market_rank">#{{ row.market_rank }} market</span>
            </td>
            <td>
              <button class="overall-btn" [ngClass]="'overall-' + (row.states[policy.key] === 'off' ? 'nothing' : row.states[policy.key])"
                      (click)="toggleCell(row, policy.key)">
                {{ row.states[policy.key] === 'blocked' ? 'Blocked' : (row.states[policy.key] === 'allowed' ? 'Allowed' : 'Not listed') }}
              </button>
            </td>
            <td class="num">{{ row.userStats ? (row.userStats.users | number) : '—' }}</td>
            <td class="num">{{ row.stats ? (row.stats.attempts | number) : '—' }}</td>
            <td class="num">{{ row.stats ? (row.stats.numbers | number) : '—' }}</td>
          </tr>
        </tbody>
      </table>
    </ng-container>

    <!-- Carrier control: free-text chips. -->
    <ng-container *ngIf="policy.code_type === 'carrier'">
      <div class="value-chips">
        <span class="value-chip" *ngFor="let value of valuesOf(policy.key)">
          {{ value }}
          <button class="chip-remove" (click)="removeValue(policy.key, value)"
                  [attr.aria-label]="'Remove ' + value">×</button>
        </span>
        <span class="value-empty" *ngIf="!valuesOf(policy.key).length">Nothing blocked.</span>
      </div>
      <div class="value-add prefix-add">
        <input matInput [(ngModel)]="newValue[policy.key]" placeholder="Carrier name"
               (keyup.enter)="addValue(policy)">
        <button mat-stroked-button (click)="addValue(policy)">Add carrier</button>
      </div>
    </ng-container>

    <!-- Prefix control: chips plus the traffic each prefix carries. -->
    <ng-container *ngIf="policy.code_type === 'prefix'">
      <div class="value-add prefix-add">
        <input matInput [(ngModel)]="newValue[policy.key]" placeholder="+1242"
               (keyup.enter)="addValue(policy)">
        <button mat-stroked-button (click)="addValue(policy)">Add prefix</button>
      </div>
      <table class="country-table prefix-table">
        <colgroup><col class="c-prefix"><col class="c-num"><col class="c-num"><col class="c-num"><col class="c-act"></colgroup>
        <thead>
          <tr>
            <th>Prefix</th>
            <th class="num">Verification attempts</th>
            <th class="num">Last {{ statsWindowDays }} days</th>
            <th class="num">Distinct numbers</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of prefixRows">
            <td class="prefix-cell">{{ row.prefix }}</td>
            <td class="num">{{ row.attempts | number }}</td>
            <td class="num" [class.hot]="row.attempts_recent > 0">{{ row.attempts_recent | number }}</td>
            <td class="num">{{ row.numbers | number }}</td>
            <td>
              <button class="overall-btn overall-blocked" (click)="removeValue(policy.key, row.prefix)">Remove</button>
            </td>
          </tr>
          <tr *ngIf="!prefixRows.length"><td colspan="5" class="empty-state">No prefixes blocked.</td></tr>
        </tbody>
      </table>
      <p class="map-note" *ngIf="stats">
        Attempts counted from {{ stats.recorded_since | timezoneDate }} across
        {{ stats.total_events | number }} recorded verification events.
      </p>
    </ng-container>
  </div>

  <!-- MAP VIEW -->
  <div class="w-100 px-3 map-view" *ngIf="view === 'map'">
    <div class="map-wrap" *ngIf="world">
      <div class="map-zoom">
        <button mat-icon-button (click)="zoomIn()" title="Zoom in"><mat-icon>add</mat-icon></button>
        <button mat-icon-button (click)="zoomOut()" title="Zoom out"><mat-icon>remove</mat-icon></button>
        <button mat-icon-button (click)="resetZoom()" title="Fit the whole world"><mat-icon>fullscreen_exit</mat-icon></button>
        <span class="zoom-level">{{ zoomLevel | number:'1.0-1' }}×</span>
      </div>

      <svg class="world" [attr.viewBox]="viewBoxAttr"
         preserveAspectRatio="xMidYMid meet"
         [class.panning]="zoomLevel > 1"
         (wheel)="onWheel($event)"
         (mousedown)="onPanStart($event)"
         (mousemove)="onPanMove($event)"
         (mouseup)="onPanEnd()"
         (mouseleave)="onPanEnd(); clearHover()">
      <rect class="ocean" x="0" y="0" [attr.width]="world.width" [attr.height]="world.height"></rect>
      <path *ngFor="let shape of mapShapes; trackBy: trackByIso3"
            [attr.d]="shape.path"
            [ngClass]="'shape-' + shape.overall"
            [class.selected]="shape.iso2 && shape.iso2 === selectedIso2"
            (click)="selectByIso3(shape.iso3)"
            (mousemove)="hoverShape(shape, $event)"
            (mouseleave)="clearHover()"></path>
      </svg>
    </div>

    <!-- Hover card: what this country is, and what it costs. -->
    <div class="map-tooltip" *ngIf="hovered as row"
         [style.left.px]="hoverX + 16" [style.top.px]="hoverY + 16">
      <div class="tip-head">
        <span class="tip-flag">{{ row.flag }}</span>
        <span class="tip-name">{{ row.name }}</span>
        <span class="tip-code">{{ row.iso2 }} · {{ row.iso3 }}<span *ngIf="row.calling_code"> · {{ row.calling_code }}</span></span>
      </div>
      <div class="tip-verdict" [ngClass]="'tip-' + row.overall">
        {{ row.overall === 'nothing' ? 'No entry' : (row.overall === 'allowed' ? 'Explicitly allowed' : 'Blocked') }}
      </div>
      <table class="tip-table">
        <tr><td>Registered users</td><td>{{ (row.userStats?.users || 0) | number }}</td></tr>
        <tr><td>Active</td><td>{{ (row.userStats?.users_active || 0) | number }}</td></tr>
        <tr><td>Active in 30 days</td><td>{{ (row.userStats?.users_active_30d || 0) | number }}</td></tr>
        <tr><td>Banned</td><td>{{ (row.userStats?.users_banned || 0) | number }}</td></tr>
        <tr class="tip-divider"><td>SMS requests</td><td>{{ (row.stats?.attempts || 0) | number }}</td></tr>
        <tr><td>Reached provider</td><td>{{ (row.stats?.sent || 0) | number }}</td></tr>
        <tr><td>Refused by a gate</td><td>{{ (row.stats?.refused || 0) | number }}</td></tr>
        <tr><td>Distinct numbers</td><td>{{ (row.stats?.numbers || 0) | number }}</td></tr>
        <tr><td>Requests, {{ statsWindowDays }}d</td><td>{{ (row.stats?.attempts_recent || 0) | number }}</td></tr>
      </table>
      <div class="tip-note" *ngIf="!row.stats && !row.userStats">No recorded traffic or users.</div>
    </div>
    <p class="map-note" *ngIf="!world">
      The map asset could not be loaded; use the list view.
    </p>
    <p class="map-note" *ngIf="world">
      Click a country to open its controls; scroll to zoom, drag to pan. Red is blocked by at least
      one control, green is on the OTP allowlist with nothing blocking it.
    </p>
  </div>

  <!-- Country detail, shared by both views. -->
  <div class="w-100 px-3" *ngIf="selectedRow as row">
    <div class="country-panel">
      <div class="panel-head">
        <span class="panel-flag">{{ row.flag }}</span>
        <div>
          <div class="panel-name">{{ row.name }}</div>
          <div class="panel-codes">
            {{ row.iso2 }} · {{ row.iso3 }} <span *ngIf="row.calling_code">· {{ row.calling_code }}</span>
            · {{ row.continent }}
            <span *ngIf="row.market_population"> · ~{{ row.market_population | number }} Filipinos</span>
          </div>
        </div>
        <div class="panel-quick">
          <button mat-stroked-button [color]="row.overall === 'nothing' ? 'primary' : null"
                  (click)="applyOverall(row, 'nothing')">No entry</button>
          <button mat-stroked-button [color]="row.overall === 'allowed' ? 'primary' : null"
                  (click)="applyOverall(row, 'allowed')">Allow</button>
          <button mat-stroked-button [color]="row.overall === 'blocked' ? 'warn' : null"
                  (click)="applyOverall(row, 'blocked')">Block</button>
          <button mat-icon-button (click)="selectCountry(row.iso2)" aria-label="Close"><mat-icon>close</mat-icon></button>
        </div>
      </div>
      <div class="panel-cells">
        <button class="cell" *ngFor="let policy of countryPolicies; trackBy: trackByKey"
                [ngClass]="'cell-' + row.states[policy.key]"
                [title]="policy.label + ' — ' + policy.description"
                (click)="toggleCell(row, policy.key)">
          <span class="cell-label">{{ policyShortLabels[policy.key] || policy.label }}</span>
          <span class="cell-state">{{ row.states[policy.key] }}</span>
        </button>
      </div>
    </div>
  </div>

  <!-- LIST VIEW: one country per row, one action per control. -->
  <div class="w-100 px-3 pb-3 list-view" *ngIf="view === 'list'">
    <ng-template #countryTable let-rows="rows">
      <!-- Fixed layout with an explicit colgroup: every section shares the same
           column widths, so the pinned table and each continent line up. -->
      <table class="country-table">
        <colgroup>
          <col class="c-country">
          <col class="c-overall">
          <col class="c-control" *ngFor="let policy of countryPolicies; trackBy: trackByKey">
          <col class="c-stat"><col class="c-stat"><col class="c-stat"><col class="c-stat">
          <col class="c-stat"><col class="c-stat"><col class="c-stat"><col class="c-wide">
        </colgroup>
        <thead>
          <tr>
            <th class="col-country">Country</th>
            <th class="col-overall">All controls</th>
            <th class="col-control" *ngFor="let policy of countryPolicies; trackBy: trackByKey"
                [title]="policy.description">
              {{ policyShortLabels[policy.key] || policy.label }}
            </th>
            <th class="num" title="Verification requests, all time">SMS req.</th>
            <th class="num" [title]="'Verification requests in the last ' + statsWindowDays + ' days'">Req. {{ statsWindowDays }}d</th>
            <th class="num" title="Distinct phone numbers seen for this country">Numbers</th>
            <th class="num" title="Registered users whose detected location is this country">Users</th>
            <th class="num" title="Distinct users reported by someone, and their share of this country">Reported</th>
            <th class="num" title="Distinct users blocked by someone, and their share of this country">Blocked</th>
            <th class="num" title="Users banned, and their share of this country">Banned</th>
            <th class="num" title="Users with at least one flagged message, and their share">Flagged</th>
            <th class="num" title="Male / female split of registered users">M / F</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of rows; trackBy: trackByIso2"
              [ngClass]="'row-' + row.overall" [class.selected]="row.iso2 === selectedIso2">
            <td class="col-country" (click)="selectCountry(row.iso2)">
              <span class="row-flag">{{ row.flag }}</span>
              <span class="row-name">{{ row.name }}</span>
              <span class="row-codes">{{ row.iso2 }}<span *ngIf="row.iso3"> · {{ row.iso3 }}</span><span *ngIf="row.calling_code"> · {{ row.calling_code }}</span></span>
              <span class="row-pin" *ngIf="row.market_rank">#{{ row.market_rank }} market</span>
            </td>
            <td class="col-overall">
              <button class="overall-btn" [ngClass]="'overall-' + row.overall" (click)="cycleCountry(row)"
                      title="Click to cycle: no entry → allow → block">
                {{ row.overall === 'nothing' ? 'No entry' : (row.overall === 'allowed' ? 'Allowed' : 'Blocked') }}
              </button>
            </td>
            <td class="col-control" *ngFor="let policy of countryPolicies; trackBy: trackByKey">
              <button class="cell-btn" [ngClass]="'cell-' + row.states[policy.key]"
                      (click)="toggleCell(row, policy.key)"
                      [title]="policy.label + ': ' + row.states[policy.key]">
                <mat-icon *ngIf="row.states[policy.key] === 'blocked'">block</mat-icon>
                <mat-icon *ngIf="row.states[policy.key] === 'allowed'">check_circle</mat-icon>
                <mat-icon *ngIf="row.states[policy.key] === 'off'">remove</mat-icon>
              </button>
            </td>
            <td class="num">{{ row.stats ? (row.stats.attempts | number) : '—' }}</td>
            <td class="num" [class.hot]="row.stats && row.stats.attempts_recent > 0">
              {{ row.stats ? (row.stats.attempts_recent | number) : '—' }}
            </td>
            <td class="num">{{ row.stats ? (row.stats.numbers | number) : '—' }}</td>
            <td class="num">{{ row.userStats ? (row.userStats.users | number) : '—' }}</td>
            <td class="num">{{ row.userStats ? share(row.userStats.users_reported, row.userStats.users) : '—' }}</td>
            <td class="num" [class.hot]="row.userStats && row.userStats.users && (row.userStats.users_blocked / row.userStats.users) > 0.25">
              {{ row.userStats ? share(row.userStats.users_blocked, row.userStats.users) : '—' }}
            </td>
            <td class="num">{{ row.userStats ? share(row.userStats.users_banned, row.userStats.users) : '—' }}</td>
            <td class="num">{{ row.userStats ? share(row.userStats.users_flagged, row.userStats.users) : '—' }}</td>
            <td class="num gender">
              <span *ngIf="row.userStats">{{ share(row.userStats.males, row.userStats.users) }}
                / {{ share(row.userStats.females, row.userStats.users) }}</span>
              <span *ngIf="!row.userStats">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </ng-template>

    <section class="group pinned" *ngIf="pinnedRows.length">
      <h3 class="group-title">
        Top markets by Filipino population
        <span class="group-count">{{ pinnedRows.length }}</span>
      </h3>
      <ng-container *ngTemplateOutlet="countryTable; context: { rows: pinnedRows }"></ng-container>
    </section>

    <section class="group" *ngFor="let group of continentGroups">
      <h3 class="group-title">{{ group.continent }} <span class="group-count">{{ group.rows.length }}</span></h3>
      <ng-container *ngTemplateOutlet="countryTable; context: { rows: group.rows }"></ng-container>
    </section>
  </div>
</div>
